import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  SimpleChanges,
  HostListener,
} from "@angular/core";
import { trigger, transition, style, animate, state } from "@angular/animations";
import { MessageType, Size } from "../types/inline-message.types";

@Component({
  selector: "ds-inline-message",
  templateUrl: "./ds-inline-message.component.html",
  styleUrls: ["./ds-inline-message.component.scss"],
  animations: [
    trigger("slideDown", [
      state(
        "expanded",
        style({
          height: "{{expanded_height}}",
          overflow: "hidden",
        }),
        {
          params: { expanded_height: 0 },
        }
      ),
      state(
        "expanded-auto",
        style({
          height: "*",
          overflow: "hidden",
        })
      ),
      state("collapsed-lg", style({ height: "24px", overflow: "hidden" })),
      state("collapsed-sm", style({ height: "20px", overflow: "hidden" })),
      transition(
        "collapsed-lg <=> expanded, collapsed-sm <=> expanded, collapsed-lg <=> expanded-auto, collapsed-sm <=> expanded-auto",
        animate("300ms ease-in-out")
      ),
    ]),
  ],
})
export class DsInlineMessageComponent implements AfterViewInit, OnDestroy {
  @Input() title: string;
  @Input() body?: string;
  @Input() type!: MessageType;
  @Input() size!: Size;
  @Input() show_text = false; // Controls whether the full text is shown
  @Input() expandable = true; // When false, never show expand/collapse arrow (always show full content)
  @Input() link?: { href?: string; routerLink?: string; queryParams?: any; label: string; target?: string };
  @Input() actionLabel?: string;
  @Input() callAction?: () => void;
  /** When true, hides the leading status icon. */
  @Input() hideIcon = false;

  icon: string;
  add_nowrap = false;
  expanded_height = "0px";
  force_show_text = false; // Used to force showing text when expanded

  @ViewChild("bodyText") bodyText!: ElementRef<HTMLElement>;
  private mutationObserver!: MutationObserver;
  private isProcessing = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.force_show_text = this.show_text;
    this.checkIcon();
  }

  @HostListener("window:resize")
  onResize() {
    this.checkLineCount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["type"]) {
      this.checkIcon();
    }
  }

  checkIcon() {
    switch (this.type) {
      case "info":
        this.icon = "info-line";
        break;
      case "warning":
        this.icon = "warning-line";
        break;
      case "danger":
        this.icon = "exclamation-line";
        break;
      case "success":
        this.icon = "check-line";
        break;
      case "default":
        this.icon = "info-line";
        break;
    }
  }

  onAction(): void {
    if (this.callAction) {
      this.callAction();
    }
  }

  ngAfterViewInit(): void {
    this.mutationObserver = new MutationObserver(() => {
      if (!this.isProcessing) {
        requestAnimationFrame(() => {
          this.checkLineCount();
        });
      }
    });
    if (this.bodyText) {
      this.mutationObserver.observe(this.bodyText.nativeElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      this.checkLineCount();
      this.calculateExpandedHeight();
    }
  }

  private createClone(): HTMLElement | null {
    if (!this.bodyText) {
      return null;
    }
    const original = this.bodyText.nativeElement;
    const clone = original.cloneNode(true) as HTMLElement;
    const computedStyle = getComputedStyle(original);
    clone.style.cssText = `
  position: absolute;
  visibility: hidden;
  white-space: normal;
  width: ${original.offsetWidth}px;
  line-height: ${computedStyle.lineHeight};
  font-size: ${computedStyle.fontSize};
  font-family: ${computedStyle.fontFamily};
  padding: ${computedStyle.padding};
  margin: ${computedStyle.margin};
  height: auto;
  white-space: normal;
  overflow: visible;
`;
    return clone;
  }

  private checkLineCount(): void {
    // Reset add_nowrap before calculation
    this.add_nowrap = false;
    // Reset show_text if not forced (collapsed state).
    if (!this.force_show_text) {
      this.show_text = false;
    }
    this.cdr.detectChanges();

    if (!this.expandable) return;

    const clone = this.createClone();
    if (!clone) return;
    document.body.appendChild(clone);

    // Calculate number of lines
    const lineHeight = parseFloat(getComputedStyle(clone).lineHeight);
    const height = clone.offsetHeight;
    const lines = Math.round(height / lineHeight);
    document.body.removeChild(clone);

    // If more than 3 lines, set add_nowrap to true
    if (lines >= 4) {
      this.add_nowrap = true;
    }
    this.isProcessing = true;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.isProcessing = false;
    });
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  showText() {
    this.force_show_text = false;
    this.show_text = !this.show_text;
    this.calculateExpandedHeight();
    this.cdr.detectChanges();
  }

  private calculateExpandedHeight(): void {
    const clone = this.createClone();
    if (!clone) return;

    document.body.appendChild(clone);

    if (!this.force_show_text) {
      this.expanded_height = `${clone.offsetHeight}px`;
    } else {
      this.expanded_height = "*";
    }

    document.body.removeChild(clone);
  }
}
